import React from "react";
import { Trans } from "react-i18next";
import { Navigate } from "react-router-dom";
import { SFBenchmarkIcon, SFTabsLayout } from "@sf-core";
import PrimaryText from "../screens/primaryText";
import SecondaryText from "../screens/secondaryText";
import NegativeText from "../screens/negativeText";
import FileUpload from "../screens/fileupload";
import FILE_UPLOAD_ENTITIES from "./fileUploadEntities";
import BenchmarkSkills from "../screens/benchmarkSkills";
import BenchmarkMicroSkills from "../screens/benchmarkMicroSkills";
import BenchmarkJFA from "../screens/benchmarkJFA";
import PATHS from "./paths";
import { Claims } from "./claims";

const ROUTES = [
  {
    path: PATHS.ANALYTICS,
    component: <Navigate to={PATHS.MANAGE} replace={true} />,
  },
  {
    path: PATHS.MANAGE,
    component: <Navigate to={PATHS.PRIMARY_TEXT_PATH} replace={true}  />,
  },
  {
    path: PATHS.BENCHMARK,
    component: <Navigate to={PATHS.BENCHMARK_SKILLS_PATH} replace={true}  />,
  },
  {
    path: PATHS.ANALYTICS,
    label: <Trans i18nKey="analyticsSkillExtractionRoutes.landing" />,
    icon: <SFBenchmarkIcon />,
    permissions: [Claims.AdminDataSkillExtractionView, Claims.AdminBenchmarkSkillsView],
    showInSideMenu: true,
    component: (
      <SFTabsLayout
        tabs={[
          {
            path: PATHS.MANAGE,
            label: (
              <Trans i18nKey="analyticsSkillExtractionRoutes.skillExtractionPaths" />
            ),
          },
          {
            path: PATHS.BENCHMARK,
            label: <Trans i18nKey="analyticsSkillExtractionRoutes.benchmark" />,
          },
        ]}
        excludeTabsFromPaths={[
          `${PATHS.PRIMARY_TEXT_PATH}/`,
          `${PATHS.SECONDARY_TEXT_PATH}/`,
          `${PATHS.NEGATIVE_TEXT_PATH}/`,
          `${PATHS.BENCHMARK_SKILLS_PATH}/`,
          `${PATHS.BENCHMARK_MICROSKILLS_PATH}/`,
          `${PATHS.BENCHMARK_JFA_PATH}/`,
        ]}
      />
    ),
    children: [
      // Manage Skills Extraction routes
      {
        path: PATHS.PRIMARY_TEXT_PATH,
        component: <PrimaryText />,
        titleSuffix: "analyticsSkillExtractionRoutes.titles.primaryText",
        permissions: [Claims.AdminDataSkillExtractionView],
      },
      {
        path: PATHS.SECONDARY_TEXT_PATH,
        component: <SecondaryText />,
        titleSuffix: "analyticsSkillExtractionRoutes.titles.secondaryText",
        permissions: [Claims.AdminDataSkillExtractionView],
      },
      {
        path: PATHS.NEGATIVE_TEXT_PATH,
        component: <NegativeText />,
        titleSuffix: "analyticsSkillExtractionRoutes.titles.negativeText",
        permissions: [Claims.AdminDataSkillExtractionView],
      },
      {
        path: PATHS.PRIMARY_TEXT_PATH_ADD,
        editing: true,
        component: (
          <FileUpload
            key="PrimaryText"
            name="Primary Text"
            entityName={FILE_UPLOAD_ENTITIES.PRIMARY_TEXT}
            finishNavigationUrl={PATHS.PRIMARY_TEXT_PATH}
          />
        ),
        titleSuffix: "analyticsSkillExtractionRoutes.add.primaryText",
        label: <Trans i18nKey="analyticsSkillExtractionRoutes.add.primaryText" />,
        permissions: [Claims.AdminDataSkillExtractionImport],
      },
      {
        path: PATHS.SECONDARY_TEXT_PATH_ADD,
        editing: true,
        component: (
          <FileUpload
            key="SecondaryText"
            name="Secondary Text"
            entityName={FILE_UPLOAD_ENTITIES.SECONDARY_TEXT}
            finishNavigationUrl={PATHS.SECONDARY_TEXT_PATH}
          />
        ),
        titleSuffix: "analyticsSkillExtractionRoutes.add.secondaryText",
        label: <Trans i18nKey="analyticsSkillExtractionRoutes.add.secondaryText" />,
        permissions: [Claims.AdminDataSkillExtractionImport],
      },
      {
        path: PATHS.NEGATIVE_TEXT_PATH_ADD,
        editing: true,
        component: (
          <FileUpload
            key="NegativeText"
            name="Negative Text"
            entityName={FILE_UPLOAD_ENTITIES.NEGATIVE_TEXT}
            finishNavigationUrl={PATHS.NEGATIVE_TEXT_PATH}
          />
        ),
        titleSuffix: "analyticsSkillExtractionRoutes.add.negativeText",
        label: <Trans i18nKey="analyticsSkillExtractionRoutes.add.negativeText" />,
        permissions: [Claims.AdminDataSkillExtractionImport],
      },
      // Benchmark routes
      {
        path: PATHS.BENCHMARK_SKILLS_PATH,
        component: <BenchmarkSkills />,
        titleSuffix: "benchmark.titles.benchmarkSkills",
        permissions: [Claims.AdminBenchmarkSkillsView],
      },
      {
        path: PATHS.BENCHMARK_MICROSKILLS_PATH,
        component: <BenchmarkMicroSkills />,
        titleSuffix: "benchmark.titles.benchmarkMicroSkills",
        permissions: [Claims.AdminBenchmarkMicroSkillsView],
      },
      {
        path: PATHS.BENCHMARK_JFA_PATH,
        component: <BenchmarkJFA />,
        titleSuffix: "benchmark.titles.benchMarkJfa",
        permissions: [Claims.AdminBenchmarkJFAView],
      },
      {
        path: PATHS.BENCHMARK_SKILLS_PATH_ADD,
        editing: true,
        component: (
          <FileUpload
            key="BenchmarkSkills"
            name="Benchmark Skills"
            entityName={FILE_UPLOAD_ENTITIES.BENCHMARK_SKILLS}
            finishNavigationUrl={PATHS.BENCHMARK_SKILLS_PATH}
          />
        ),
        titleSuffix: "benchmark.add.benchMarkSkills",
        label: <Trans i18nKey="benchmark.add.benchMarkSkills" />,
        permissions: [Claims.AdminBenchmarkSkillsImport],
      },
      {
        path: PATHS.BENCHMARK_MICROSKILLS_PATH_ADD,
        editing: true,
        component: (
          <FileUpload
            key="BenchmarkMicroSkills"
            name="Benchmark MicroSkills"
            entityName={FILE_UPLOAD_ENTITIES.BENCHMARK_MICROSKILLS}
            finishNavigationUrl={PATHS.BENCHMARK_MICROSKILLS_PATH}
          />
        ),
        titleSuffix: "benchmark.add.benchMarkMicroSkills",
        label: <Trans i18nKey="benchmark.add.benchMarkMicroSkills" />,
        permissions: [Claims.AdminBenchmarkMicroSkillsImport],
      },
      {
        path: PATHS.BENCHMARK_JFA_PATH_ADD,
        editing: true,
        component: (
          <FileUpload
            key="BenchmarkJfa"
            name="Benchmark JFA"
            entityName={FILE_UPLOAD_ENTITIES.BENCHMARK_JFA}
            finishNavigationUrl={PATHS.BENCHMARK_JFA_PATH}
          />
        ),
        titleSuffix: "benchmark.add.benchMarkJfa",
        label: <Trans i18nKey="benchmark.add.benchMarkJfa" />,
        permissions: [Claims.AdminBenchmarkJFAImport],
      },
    ],
  },
];

export default ROUTES;
